<template>
  <section 
    id="qualifications-training" 
    class="w-full py-20 bg-[#FAF9F7] font-poppins"
    style="position: relative; z-index: 3;"
  >
    <div class="container mx-auto px-4 md:px-8 lg:px-16 max-w-[1600px] relative z-10">
      <!-- Section title with refined styling -->
      <div class="mb-16 text-center">
        <h2 class="text-4xl md:text-5xl font-bold text-[#333333] mb-4">Qualifications & Training</h2>
        <div class="h-1 w-24 mx-auto bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
        <p class="text-center text-[#333333] mt-6 text-lg max-w-3xl mx-auto">
          Professional certifications and specialised training that inform my therapeutic approach.
        </p>
      </div>
      
      <!-- CORE QUALIFICATIONS SECTION -->
      <div class="mb-24">
        <div class="flex items-center justify-center mb-12">
          <div class="px-6 py-3 bg-gradient-to-r from-[#D2919A]/10 to-[#D2919A]/5 rounded-full">
            <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
              Core Qualifications
              <span class="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- BACP Certificate - Improved card design -->
          <div class="qual-card group">
            <div class="qual-card-inner">
              <div class="mb-6 flex flex-col items-center">
                <img src="../assets/bacp.jpeg" alt="BACP Badge" class="h-16 w-auto object-contain rounded-md overflow-hidden mb-4 transition-transform duration-500 group-hover:scale-105" />
                <h4 class="text-xl font-semibold text-[#D2919A] text-center relative pb-2">
                  BACP Certificate of Proficiency
                  <span class="absolute -bottom-px left-1/4 right-1/4 h-[2px] bg-gradient-to-r from-transparent via-[#D2919A]/50 to-transparent"></span>
                </h4>
              </div>
              <p class="text-[#333333] text-center leading-relaxed">Professional standard of competence recognised by the British Association for Counselling and Psychotherapy</p>
            </div>
          </div>
          
          <!-- MBACP Registered -->
          <div class="qual-card group">
            <div class="qual-card-inner">
              <div class="mb-6 flex flex-col items-center">
                <div class="qual-icon mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h4 class="text-xl font-semibold text-[#D2919A] text-center relative pb-2">
                  MBACP Registered Member
                  <span class="absolute -bottom-px left-1/4 right-1/4 h-[2px] bg-gradient-to-r from-transparent via-[#D2919A]/50 to-transparent"></span>
                </h4>
              </div>
              <p class="text-[#333333] text-center">Registered member of the British Association for Counselling and Psychotherapy</p>
            </div>
          </div>
          
          <!-- Counselling Practice Diploma -->
          <div class="qual-card group">
            <div class="qual-card-inner">
              <div class="mb-6 flex flex-col items-center">
                <div class="qual-icon mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h4 class="text-xl font-semibold text-[#D2919A] text-center relative pb-2">
                  Counselling Practice Diploma Level 4
                  <span class="absolute -bottom-px left-1/4 right-1/4 h-[2px] bg-gradient-to-r from-transparent via-[#D2919A]/50 to-transparent"></span>
                </h4>
              </div>
              <p class="text-[#333333] text-center leading-relaxed">Professional qualification covering advanced counselling theory and practice</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- ESSENTIAL REQUIREMENTS SECTION -->
      <div class="mb-24">
        <div class="flex items-center justify-center mb-12">
          <div class="px-6 py-3 bg-gradient-to-r from-[#D2919A]/10 to-[#D2919A]/5 rounded-full">
            <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
              Essential Requirements
              <span class="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Enhanced DBS Check -->
          <div class="req-card">
            <div class="req-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h4 class="req-title">Enhanced DBS Check</h4>
              <p class="text-[#333333] text-sm leading-relaxed">Criminal record check for working with vulnerable individuals</p>
            </div>
          </div>
          
          <!-- Professional Insurance -->
          <div class="req-card">
            <div class="req-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h4 class="req-title">Professional Insurance</h4>
              <p class="text-[#333333] text-sm leading-relaxed">Full professional liability and indemnity insurance</p>
            </div>
          </div>
          
          <!-- Regular Supervision -->
          <div class="req-card">
            <div class="req-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h4 class="req-title">Regular Supervision</h4>
              <p class="text-[#333333] text-sm leading-relaxed">Ongoing professional supervision to ensure ethical practice</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- ADDITIONAL TRAINING SECTION -->
      <div>
        <div class="flex flex-col items-center justify-center mb-12">
          <div class="px-6 py-3 bg-gradient-to-r from-[#D2919A]/10 to-[#D2919A]/5 rounded-full mb-4">
            <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
              Additional Training
              <span class="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
          </div>
          <p class="text-[#333333] text-center max-w-2xl">Specialised training that enhances my clinical skills and theoretical understanding.</p>
        </div>
        
        <!-- Training filter tabs -->
        <div class="mb-10">
          <div class="flex flex-wrap justify-center gap-2 px-2" ref="trainingFilters">
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'all'}"
              @click="setFilter('all')"
            >
              All
            </button>
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'counselling'}"
              @click="setFilter('counselling')"
            >
              Counselling
            </button>
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'mental-health'}"
              @click="setFilter('mental-health')"
            >
              Mental Health
            </button>
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'neuro'}"
              @click="setFilter('neuro')"
            >
              Neurodiversity
            </button>
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'specialised'}"
              @click="setFilter('specialised')"
            >
              Specialised
            </button>
            <button 
              class="training-filter-btn"
              :class="{'active': trainingFilter === 'child'}"
              @click="setFilter('child')"
            >
              Child & Youth
            </button>
          </div>
        </div>
        
        <!-- Training items grid with preview and fade effect -->
        <div class="flex flex-wrap justify-center" ref="trainingGridWrapper">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 relative" ref="trainingGrid">
            <!-- Show only first 3 training items when not showing all -->
            <template v-if="!showAllTrainingItems && trainingFilter === 'all'">
              <div v-for="(item, index) in visibleTrainingItems.slice(0, 3)" :key="index" class="training-card">
                <div class="training-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6v12m-8-6h16" />
                  </svg>
                </div>
                <h4 class="training-card-title">{{ item.title }}</h4>
                <p class="training-card-desc">{{ item.description }}</p>
              </div>
              
              <!-- Gradient fade overlay for preview -->
              <div class="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-[#FAF9F7]/60 to-[#FAF9F7]"></div>
            </template>
            
            <!-- Show all training items when show all is clicked or when filtering -->
            <template v-else>
              <div 
                v-for="(item, index) in visibleTrainingItems" 
                :key="index" 
                class="training-card"
              >
                <div class="training-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6v12m-8-6h16" />
                  </svg>
                </div>
                <h4 class="training-card-title">{{ item.title }}</h4>
                <p class="training-card-desc">{{ item.description }}</p>
              </div>
            </template>
            
            <!-- Message for no items -->
            <div v-if="noVisibleTrainingItems" class="col-span-full text-center py-10">
              <p class="text-[#333333] text-lg">No training items in this category. Please select another filter.</p>
            </div>
          </div>
        </div>
        
        <!-- "Show All" button (only when needed) -->
        <div v-if="!showAllTrainingItems && trainingFilter === 'all'" class="mt-8 text-center">
          <button 
            @click="showAllTrainingItems = true"
            class="px-5 py-2.5 bg-[#D2919A] text-white border border-[#D2919A] rounded-full font-medium hover:bg-[#D2919A]/90 transition-all duration-300 shadow-sm hover:shadow"
          >
            Show All Training
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        <!-- "Show Less" button (only when showing all) -->
        <div v-if="showAllTrainingItems && trainingFilter === 'all'" class="mt-8 text-center">
          <button 
            @click="showAllTrainingItems = false"
            class="px-5 py-2.5 bg-[#D2919A] text-white border border-[#D2919A] rounded-full font-medium hover:bg-[#D2919A]/90 transition-all duration-300 shadow-sm hover:shadow"
          >
            Show Less
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from '@gsap/shockingly'
import { ScrollTrigger } from '@gsap/shockingly/ScrollTrigger'

export default {
  name: 'QualificationsTrainingSection',
  data() {
    return {
      expandedQualification: null,
      expandedDetails: null,
      trainingFilter: 'all',
      showAllTrainingItems: false,
      isTabSwitching: false,
      trainingFiltersAnimation: null,
      trainingGridAnimation: null,
      trainingItems: [
        { 
          id: 'cert3', 
          category: 'counselling',
          title: 'Counselling Certificate Level 3',
          description: 'Advanced qualification covering therapeutic approaches and counselling theory, providing you with a counsellor who has in-depth understanding of the counselling process.',
          order: 0 
        },
        { 
          id: 'cert2', 
          category: 'counselling',
          title: 'Counselling Skills Certificate Level 2',
          description: 'Foundation in active listening, reflective practice and essential counselling skills to build a strong therapeutic relationship with you.',
          order: 1 
        },
        { 
          id: 'mental-health-problems', 
          category: 'mental-health',
          title: 'NCFE Awareness of Mental Health Problems Level 2',
          description: 'In-depth understanding of various mental health conditions, helping me to better appreciate and support your unique experiences and challenges.',
          order: 2 
        },
        { 
          id: 'ace', 
          category: 'specialised',
          title: 'NCFE Understanding Adverse Childhood Experiences Level 2',
          description: 'Knowledge of how early life experiences can impact adult well-being, allowing me to provide trauma-informed care that acknowledges your life journey.',
          order: 3 
        },
        { 
          id: 'youth-mental', 
          category: 'child',
          title: 'NCFE Understanding Children and Young People Mental Health Level 2',
          description: 'Specialised training in supporting young people\'s emotional wellbeing, helping me create a safe space for children and adolescents to explore their feelings.',
          order: 4 
        },
        { 
          id: 'autism', 
          category: 'neuro',
          title: 'NFCE Understanding Autism Level 2',
          description: 'Comprehensive understanding of autism spectrum conditions, enabling me to tailor my approach to support your unique neurodivergent experiences.',
          order: 5 
        },
        { 
          id: 'neurodev', 
          category: 'neuro',
          title: 'NFCE Introduction to Neurodevelopment in the Early Years Level 2',
          description: 'Knowledge about early brain development and its impact on behavior and emotional regulation, helping me understand developmental factors in your current experiences.',
          order: 6 
        },
        { 
          id: 'safeguarding', 
          category: 'child',
          title: 'Safeguarding Level 2',
          description: 'Comprehensive training to identify risks and ensure safety, especially for vulnerable clients, providing you with a secure therapeutic environment.',
          order: 7 
        },
        { 
          id: 'nspcc', 
          category: 'child',
          title: 'NSPCC Child Protection: An Introduction',
          description: 'Specialised training from the UK\'s leading children\'s charity, equipping me with the skills to support young people through difficult experiences safely and ethically.',
          order: 8 
        },
        { 
          id: 'thrive', 
          category: 'specialised',
          title: 'Thrive - Emotional Health Practitioner',
          description: 'Training in a developmental approach that supports emotional resilience and social skills, helping you build strategies for long-term emotional wellbeing.',
          order: 9 
        },
        { 
          id: 'suicide-prevention', 
          category: 'mental-health',
          title: 'Suicide Prevention Training',
          description: 'Specialised training in recognizing warning signs and supporting individuals at risk, ensuring I can provide effective crisis intervention when needed.',
          order: 10 
        },
        { 
          id: 'trauma-informed', 
          category: 'specialised',
          title: 'Trauma-Informed Practice',
          description: 'Training that acknowledges the widespread impact of trauma and helps me create a therapeutic environment that promotes safety, trustworthiness, and empowerment.',
          order: 11 
        },
        { 
          id: 'attachment-theory', 
          category: 'counselling',
          title: 'Attachment Theory in Practice',
          description: 'Understanding how early relationship patterns affect current relationships, helping me support you in developing secure connections with others.',
          order: 12 
        },
        { 
          id: 'cbt-fundamentals', 
          category: 'counselling',
          title: 'CBT Fundamentals',
          description: 'Training in cognitive-behavioral techniques that help identify and challenge unhelpful thought patterns, offering practical tools for managing difficulties.',
          order: 13 
        },
        { 
          id: 'adhd-awareness', 
          category: 'neuro',
          title: 'ADHD Awareness and Support',
          description: 'Specialised understanding of attention deficit hyperactivity disorder, allowing me to adapt my approach to support your unique cognitive style.',
          order: 14 
        },
        { 
          id: 'domestic-abuse', 
          category: 'specialised',
          title: 'Domestic Abuse Awareness',
          description: 'Training to recognize and respond appropriately to those affected by domestic abuse, creating a safe space for processing related trauma.',
          order: 15 
        }
      ],
      animationsInitialized: false
    }
  },
  computed: {
    noVisibleTrainingItems() {
      return this.trainingFilter !== 'all' && this.filteredTrainingItems === 0;
    },
    filteredTrainingItems() {
      if (this.trainingFilter === 'all') {
        return this.trainingItems.length;
      } else {
        return this.trainingItems.filter(item => item.category === this.trainingFilter).length;
      }
    },
    visibleTrainingItems() {
      if (this.trainingFilter === 'all') {
        return this.trainingItems;
      } else {
        return this.trainingItems.filter(item => item.category === this.trainingFilter);
      }
    }
  },
  mounted() {
    this.initAnimations();
    
    // Set a default filter to ensure all items show
    this.trainingFilter = 'all';
    
    // Force items to be visible after a delay in case animations fail
    setTimeout(() => {
      const items = document.querySelectorAll('.training-grid-item');
      items.forEach(item => {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      });
    }, 1000);
  },
  beforeUnmount() {
    window.removeEventListener('heroVideoLocked', this.initAnimations);
    
    // Clean up animations
    if (this.trainingFiltersAnimation) {
      this.trainingFiltersAnimation.kill();
    }
    if (this.trainingGridAnimation) {
      this.trainingGridAnimation.kill();
    }
    
    // Clean up any ScrollTrigger instances
    if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars.id && trigger.vars.id.startsWith('qual-')) {
          trigger.kill();
        }
      });
    }
  },
  methods: {
    initAnimations() {
      // Only proceed if GSAP is available
      if (typeof gsap === 'undefined') {
        console.warn('GSAP not available for animations');
        return;
      }
      
      console.log('Initializing QualificationsTrainingSection animations');
      
      // Animate in the section title
      gsap.from('.training-section-title', {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power2.out',
        onComplete: () => {
          this.animateFilterButtons();
          this.animateGridItems();
        }
      });
    },
    
    // Separate method to animate filter buttons
    animateFilterButtons() {
      if (typeof gsap === 'undefined') return;
      
      // Use Vue ref instead of querySelector for better reliability
      if (this.$refs.trainingFilters && this.$refs.trainingFilters.children.length) {
        const filterBtns = Array.from(this.$refs.trainingFilters.children);
        
        // Clear any existing animations first
        gsap.killTweensOf(filterBtns);
        
        // Set initial state and then animate
        gsap.set(filterBtns, { opacity: 0, y: 20 });
        gsap.to(filterBtns, {
          opacity: 1,
          y: 0,
          stagger: 0.05, // Faster stagger for smoother animation
          duration: 0.4, // Slightly faster animation
          ease: "power2.out",
          clearProps: "all" // Clear properties after animation to avoid interference
        });
      }
    },
    
    // Separate method to animate grid items
    animateGridItems() {
      if (typeof gsap === 'undefined') return;
      
      if (this.$refs.trainingGrid) {
        const gridItems = this.$refs.trainingGrid.querySelectorAll('.training-card');
        
        if (gridItems.length) {
          // Clear any existing animations
          gsap.killTweensOf(gridItems);
          
          // Reset initial state
          gsap.set(gridItems, { opacity: 0, y: 20 });
          
          // Animate with a smoother timing
          this.trainingGridAnimation = gsap.to(gridItems, {
            opacity: 1,
            y: 0,
            stagger: {
              amount: 0.4, // Total stagger time across all elements
              grid: "auto",
              from: "start"
            },
            duration: 0.4,
            ease: "power1.out",
            clearProps: "transform", // Keep opacity but clear transform to avoid layout issues
            onComplete: () => {
              // Ensure all items are fully visible when animation completes
              gridItems.forEach(item => {
                item.style.opacity = '1';
              });
            }
          });
        }
      }
    },
    
    setFilter(filter) {
      // Don't do anything if clicking on the already active filter
      if (this.trainingFilter === filter) return;
      
      // Store previous filter for transition effect
      const previousFilter = this.trainingFilter;
      this.trainingFilter = filter;
      this.isTabSwitching = true;
      
      // Ensure the DOM has updated with the new filtered items
      this.$nextTick(() => {
        // Kill any ongoing animations to prevent conflicts
        if (this.trainingGridAnimation) {
          this.trainingGridAnimation.kill();
        }
        
        // Fade out existing items first if switching categories
        if (previousFilter !== filter && this.$refs.trainingGrid) {
          // Brief fade-out of current items before showing new ones
          gsap.to(this.$refs.trainingGrid.children, {
            opacity: 0,
            y: 10,
            duration: 0.2,
            onComplete: () => {
              // Then show new items after DOM has updated
              this.$nextTick(() => {
                this.animateGridItems();
                
                // Reset the tab switching state
                setTimeout(() => {
                  this.isTabSwitching = false;
                }, 300);
              });
            }
          });
        } else {
          // No fade needed, just animate in
          this.animateGridItems();
          
          // Reset the tab switching state
          setTimeout(() => {
            this.isTabSwitching = false;
          }, 300);
        }
      });
    },
    
    showTrainingItem(category) {
      return this.trainingFilter === 'all' || this.trainingFilter === category;
    },
    
    refreshTrainingItems() {
      if (!this.$refs.trainingGrid) return;
      
      // Clear any existing animations
      if (this.trainingGridAnimation) {
        this.trainingGridAnimation.kill();
      }
      
      // Reset all items
      gsap.set(this.$refs.trainingGrid.children, {
        clearProps: "opacity,y,position,zIndex,pointerEvents"
      });
      
      // Get visible items based on current filter
      const visibleItems = Array.from(this.$refs.trainingGrid.children).filter(
        item => !item.classList.contains('pointer-events-none') && 
               window.getComputedStyle(item).display !== 'none'
      );
      
      // Center the grid if there are just a few items
      if (this.$refs.trainingGridWrapper) {
        if (visibleItems.length <= 3) {
          this.$refs.trainingGridWrapper.classList.add('justify-center');
        } else {
          this.$refs.trainingGridWrapper.classList.remove('justify-center');
        }
      }
      
      // Animate in the visible items
      if (visibleItems.length > 0) {
        this.trainingGridAnimation = gsap.fromTo(visibleItems, 
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            stagger: {
              each: 0.08,
              grid: "auto",
              from: "start"
            },
            duration: 0.5,
            ease: "power2.out",
            scrollTrigger: {
              id: 'qual-training-grid',
              trigger: this.$refs.trainingGrid,
              start: 'top 85%',
            }
          }
        );
        
        // Force items to be visible even if animation fails
        setTimeout(() => {
          visibleItems.forEach(item => {
            if (parseFloat(window.getComputedStyle(item).opacity) < 0.9) {
              item.style.opacity = "1";
              item.style.transform = "translateY(0)";
            }
          });
        }, 1000);
      }
      
      // Failsafe: Make all items visible after a timeout
      setTimeout(() => {
        // Force all Core Qualifications to be visible
        document.querySelectorAll('.qual-card').forEach(card => {
          card.style.opacity = "1";
          card.style.transform = "translateY(0)";
        });
        
        // Force all Essential Requirements to be visible
        document.querySelectorAll('.req-card').forEach(card => {
          card.style.opacity = "1";
          card.style.transform = "translateX(0)";
        });
        
        // Force all training items to be visible
        document.querySelectorAll('.training-card').forEach(card => {
          if (window.getComputedStyle(card).display !== 'none') {
            card.style.opacity = "1";
            card.style.transform = "translateY(0)";
          }
        });
      }, 2000);
    }
  }
}
</script>

<style scoped>
/* Qualification Cards */
.qual-card {
  @apply bg-white rounded-lg p-6 transition-all duration-300 relative overflow-hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(210, 145, 154, 0.15);
  transform: translateY(0);
}

.qual-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 5px 10px rgba(0, 0, 0, 0.02);
  border-color: rgba(210, 145, 154, 0.3);
}

.qual-card-inner {
  @apply relative h-full;
}

.qual-icon {
  @apply w-16 h-16 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center shadow-sm mx-auto;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.qual-card:hover .qual-icon {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Requirement Cards */
.req-card {
  @apply flex items-start p-4 rounded-lg bg-white transition-all duration-300;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(210, 145, 154, 0.15);
}

.req-card:hover {
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  border-color: rgba(210, 145, 154, 0.3);
  transform: translateY(-3px);
}

.req-icon {
  @apply h-10 w-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center mr-4 flex-shrink-0 shadow-sm;
  transition: transform 0.3s ease;
}

.req-card:hover .req-icon {
  transform: scale(1.1);
}

.req-title {
  @apply text-lg font-semibold text-[#D2919A] mb-1 relative pb-2;
}

.req-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30%;
  height: 2px;
  background: linear-gradient(to right, rgba(210, 145, 154, 0.7), rgba(210, 145, 154, 0.1));
  transition: width 0.3s ease;
}

.req-card:hover .req-title::after {
  width: 50%;
}

/* Training Cards */
.training-card {
  @apply bg-white rounded-lg p-5 transition-all duration-300 hover:shadow-md relative;
  border: 1px solid rgba(210, 145, 154, 0.15);
  transform: translateY(0);
}

.training-card:hover {
  transform: translateY(-5px);
  border-color: rgba(210, 145, 154, 0.3);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
}

.training-card-icon {
  @apply w-12 h-12 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center mb-4 shadow-sm;
  transition: transform 0.3s ease;
}

.training-card:hover .training-card-icon {
  transform: scale(1.1);
}

.training-card-title {
  @apply text-lg font-semibold text-[#D2919A] mb-2 relative;
}

.training-card-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -5px;
  width: 30%;
  height: 2px;
  background: linear-gradient(to right, rgba(210, 145, 154, 0.7), rgba(210, 145, 154, 0.1));
  transition: width 0.3s ease;
}

.training-card:hover .training-card-title::after {
  width: 50%;
}

.training-card-desc {
  @apply text-[#333333] text-sm md:text-base;
}

/* Training filter buttons */
.training-filter-btn {
  @apply px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap;
  @apply bg-white/80 text-[#333333] border border-[#D2919A]/30;
  /* Improve transitions for smoother effect */
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.training-filter-btn:hover {
  @apply bg-[#D2919A]/10;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0,0,0,0.08);
}

.training-filter-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.training-filter-btn.active {
  @apply bg-[#D2919A] text-white;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(210, 145, 154, 0.3);
}
</style>